import React from 'react';
import PropTypes from 'prop-types';

import {
  Box, Text, Heading, Button,
} from 'grommet';
import { FormClose } from 'grommet-icons';

import {
  StyledFormInput,
  StyledFormInputClear,
  StyledRaisedContainer,
  StyledSmallButton,
} from './StyledProductFormComponent';


export const FormSectionWrapper = ({
  small, sectionBg, sectionTitle, titleColor, children, boxStyle = {},
}) => (
  <Box
    flex={!small ? '1' : undefined}
    minWidth={!small ? '0' : undefined}
    height="fit-content"
    direction="column"
    round="var(--app-radius)"
    pad={{ top: '1.5rem', bottom: '1rem', horizontal: small ? '0.5rem' : '1rem' }}
    background={sectionBg}
    style={boxStyle}
  >
    <Box justify="center" align="center">
      <Heading level={4} margin="none" size="1rem" color={titleColor} textAlign="center">
        {sectionTitle}
      </Heading>
    </Box>
    <Box>
      {children}
    </Box>
  </Box>
);

FormSectionWrapper.propTypes = {
  small: PropTypes.bool.isRequired,
  sectionBg: PropTypes.string.isRequired,
  sectionTitle: PropTypes.string.isRequired,
  titleColor: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  boxStyle: PropTypes.object,
};

export const FormFieldContainer = ({
  small,
  fieldTitle = null,
  description = null,
  limit = null,
  charLimit = null,
  inputConfig = null,
  childGap = null,
  primaryText = 'var(--page-primary-text)',
  hintText = 'var(--page-hint-text)',
  children,
  useClassName = '',
}) => {
  let displayDescription = description;

  if (limit) displayDescription = displayDescription.replace('{{limit}}', limit);
  if (charLimit) displayDescription = displayDescription.replace('{{char_limit}}', charLimit);

  let customHint = null;

  if (inputConfig?.promptPageVisit) {
    customHint = 'configure options';
  }

  const renderDescription = () => {
    if (displayDescription && customHint) {
      return (
        <>
          {displayDescription}
          {' '}
          (
          <Button plain href={inputConfig?.promptPageVisit} target="_blank" rel="noreferrer">
            <Text size={small ? '0.85rem' : '0.75rem'} color="var(--page-highlight)">
              {customHint}
            </Text>
          </Button>
          )
        </>
      );
    }

    return displayDescription;
  };

  return (
    <StyledRaisedContainer
      direction="column"
      gap="0.75rem"
      pad={{ horizontal: '1.5rem', vertical: '1rem' }}
      margin={{ top: '1rem' }}
      className={useClassName}
    >
      {(fieldTitle || displayDescription) && (
        <Box direction="column" gap="0.25rem">
          {fieldTitle && (
            <Text weight={600} size="1rem" color={primaryText}>
              {fieldTitle}
            </Text>
          )}
          {displayDescription && (
            <Text size={small ? '0.85rem' : '0.75rem'} color={hintText}>
              {renderDescription()}
            </Text>
          )}
        </Box>
      )}
      <Box gap={childGap ?? '0'} direction="column">
        {children}
      </Box>
    </StyledRaisedContainer>
  );
};

FormFieldContainer.propTypes = {
  small: PropTypes.bool.isRequired,
  fieldTitle: PropTypes.string,
  description: PropTypes.string,
  limit: PropTypes.number,
  charLimit: PropTypes.number,
  primaryText: PropTypes.string,
  hintText: PropTypes.string,
  children: PropTypes.node.isRequired,
  childGap: PropTypes.string,
  useClassName: PropTypes.string,
  inputConfig: PropTypes.shape({
    promptPageVisit: PropTypes.string,
  }),
};

export const FormInput = React.forwardRef(({
  onClear = null, clearActive = false, ...rest
}, ref) => {
  if (onClear) {
    return (
      <Box className="relative">
        <StyledFormInput
          plain
          ref={ref}
          padRight={clearActive ? '1.9rem' : null}
          {...rest}
        />
        <StyledFormInputClear
          plain
          icon={<FormClose size="1.25rem" color="var(--button-icon-color)" />}
          onClick={onClear}
          style={clearActive ? null : { display: 'none' }}
        />
      </Box>
    );
  }

  return <StyledFormInput plain ref={ref} {...rest} />;
});

FormInput.propTypes = {
  onClear: PropTypes.func,
  clearActive: PropTypes.bool,
};

export const SingleTextInput = ({
  focusHighlight = 'var(--page-focus-highlight)',
  value = '',
  handleFormValues,
  ...rest
}) => (
  <FormInput
    value={value}
    onChange={(e) => handleFormValues(e.target.value)}
    highlight={focusHighlight}
    {...rest}
  />
);

SingleTextInput.propTypes = {
  focusHighlight: PropTypes.string,
  handleFormValues: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const AddButton = ({
  disabled = false, highlight, ...rest
}) => {
  const buttonLabelColor = disabled ? 'var(--button-disabled-text)' : highlight;

  return (
    <StyledSmallButton plain alignSelf={rest.buttonAlign || 'start'} disabled={disabled} highlight={highlight} {...rest}>
      <Box
        direction="row"
        round="var(--app-radius)"
        align="center"
        background={disabled ? 'var(--button-disabled-bg)' : 'white'}
        border={{ size: '1px', color: 'var(--button-border)', style: 'solid' }}
        pad={{ vertical: '0.35rem', horizontal: '0.85rem' }}
        gap="0.4rem"
      >
        <Text weight={600} textAlign="center" size="0.8rem" color={buttonLabelColor}>
          + Add
        </Text>
      </Box>
    </StyledSmallButton>
  );
};

AddButton.propTypes = {
  disabled: PropTypes.bool,
  highlight: PropTypes.string.isRequired,
};

export const TagButton = ({
  tag, highlight = 'var(--page-alt-highlight)', textColor, ...rest
}) => (
  <StyledSmallButton
    plain
    alignSelf="start"
    margin="0.125rem"
    highlight={highlight}
    {...rest}
  >
    <Box
      direction="row"
      round="0.25rem"
      align="center"
      background="white"
      border={{ size: '1px', color: highlight, style: 'solid' }}
      pad={{ vertical: '0.25rem', right: '0.5rem', left: '0.75rem' }}
      gap="0.75rem"
    >
      <Box>
        <Text textAlign="center" size="1rem" color={textColor}>
          {tag.label}
        </Text>
      </Box>
      <FormClose size="1.25rem" color="var(--button-icon-color)" />
    </Box>
  </StyledSmallButton>
);

TagButton.propTypes = {
  tag: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
  highlight: PropTypes.string,
  textColor: PropTypes.string.isRequired,
};
